import { motion, useReducedMotion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "@/lib/i18n";
import { useIsMobile } from "@/hooks/use-mobile";

function AnimatedNumber({ target, inView, prefersReduced }: { target: string; inView: boolean; prefersReduced: boolean | null }) {
  const [display, setDisplay] = useState("00");

  useEffect(() => {
    if (!inView) return;
    if (prefersReduced) { setDisplay(target); return; }
    const num = parseInt(target);
    if (isNaN(num)) { setDisplay(target); return; }
    let current = 0;
    const duration = 600;
    const interval = duration / num;
    const timer = setInterval(() => {
      current++;
      setDisplay(current.toString().padStart(2, "0"));
      if (current >= num) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [inView, target, prefersReduced]);

  return <>{display}</>;
}

function ProcessStep({ step, index, prefersReduced, isMobile }: { step: { num: string; title: string; desc: string }; index: number; prefersReduced: boolean | null; isMobile: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <div ref={ref} className="relative">
      <motion.div
        initial={prefersReduced || isMobile ? { opacity: 0 } : { opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ delay: prefersReduced ? 0 : index * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="pt-6 border-t border-border"
      >
        <motion.span
          className="text-4xl font-display text-primary inline-block mb-4"
          animate={inView && !prefersReduced ? {
            boxShadow: ["0 0 0 4px hsl(var(--primary) / 0.4)", "0 0 0 0px transparent"],
          } : undefined}
          transition={{ duration: 1, delay: 0.3 }}
          style={{ borderRadius: "8px", padding: "0 4px" }}
        >
          <AnimatedNumber target={step.num} inView={inView} prefersReduced={prefersReduced} />
        </motion.span>
        <h3 className="uppercase font-body font-bold tracking-widest text-sm text-foreground mb-3">
          {step.title}
        </h3>
        <p className="text-sm font-body text-muted-foreground leading-relaxed">{step.desc}</p>
      </motion.div>
    </div>
  );
}

const ProcessSection = () => {
  const prefersReduced = useReducedMotion();
  const isMobile = useIsMobile();
  const t = useTranslation();
  const p = t.aiTeams.process;

  const anim = {
    initial: prefersReduced || isMobile ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  return (
    <section id="process" className="py-16 bg-transparent">
      <div className="container mx-auto px-6">
        <motion.div
          initial={anim.initial}
          whileInView={anim.whileInView}
          viewport={anim.viewport}
          transition={anim.transition}
          className="mb-16"
        >
          <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">{p.eyebrow}</p>
          <h2 className="text-4xl sm:text-5xl font-display tracking-tight">
            {p.heading}
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {p.steps.map((step, i) => (
            <ProcessStep
              key={step.num}
              step={step}
              index={i}
              prefersReduced={prefersReduced}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
